import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminService, enterpriseAdminService, reportTypeService } from '../services';
import ProfileDetails from './ProfileDetails';
import { SPECIALTIES } from '../constants/specialties';
import { useLoading } from '../context/LoadingContext';
import './Admin.css';
import { createLogger } from '../services/Debug';

function AdminDashboard() {
  const { profile } = useAuth();
  const log = createLogger('AdminDashboard');
  const { setLoading: setGlobalLoading } = useLoading();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState({});
  
  // Enterprise management state
  const [enterprises, setEnterprises] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [enterpriseAdmins, setEnterpriseAdmins] = useState([]);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'enterprises'
  const [showCreateEnterprise, setShowCreateEnterprise] = useState(false);
  const [newEnterprise, setNewEnterprise] = useState({
    name: '',
    shortName: '',
    description: '',
    maxLicenses: 10,
    specialty: ''
  });
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  // Raw JSON payload for each user as returned by the API
  const [rawUsersData, setRawUsersData] = useState([]);

  // Track which user rows are expanded to show detailed profile information
  const [expandedRows, setExpandedRows] = useState({});

  // Prompt preview state
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedPreviewReportType, setSelectedPreviewReportType] = useState('');
  const [promptPreview, setPromptPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Broadcast email state
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastHtmlContent, setBroadcastHtmlContent] = useState('');
  const [broadcastTextContent, setBroadcastTextContent] = useState('');
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [composerMode, setComposerMode] = useState('visual'); // 'visual' or 'html'

  // Toggle expand/collapse for a specific user row
  const toggleExpandRow = (userId) => {
    setExpandedRows(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  /* ---------------------------------------------
   * Admin permission handling (new logic)
   * -------------------------------------------*/
  

  const isAdmin = profile?.isAdmin || false;

  useEffect(() => {
    // Set loading immediately on mount with 500ms minimum
    setGlobalLoading({
      isLoading: true,
      text: "Chargement du tableau de bord administrateur...",
      showLogo: true
    });
    
    const initializeData = async () => {
      const minimumLoadingTime = 500;
      const startTime = Date.now();
      
      try {
        await Promise.all([
          loadUsers(),
          loadEnterprises(),
          loadReportTypes()
        ]);
        
        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      } finally {
        setGlobalLoading(false);
      }
    };
    
    initializeData();
  // The dependency array intentionally left empty to mimic componentDidMount behaviour.
  // isAdmin is derived from a static profileManager call above.
  }, []);

  // Debug useEffect to monitor enterprises state changes
  useEffect(() => {
    log.debug('Enterprises state changed:', enterprises);
  }, [enterprises]);

  // Remove the restriction - allow all admins to access enterprises tab
  // No need to reset tab anymore since all admins can access enterprises

  const loadUsers = async () => {
    try {
      const data = await adminService.getUsers();
      const list = data.users || data;
      setUsers(list);
      // If PaginatedUsers exposes rawUsers we keep it, else fallback
      if (data.rawUsers) {
        setRawUsersData(data.rawUsers);
      } else if (Array.isArray(list)) {
        setRawUsersData(list);
      }
    } catch (error) {
      log.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReportTypes = async () => {
    try {
      const types = await reportTypeService.getNames();
      let normalizedTypes = [];
      if (Array.isArray(types)) {
        normalizedTypes = types;
      } else if (Array.isArray(types?.names)) {
        normalizedTypes = types.names;
      } else if (Array.isArray(types?.reportTypes)) {
        normalizedTypes = types.reportTypes;
      } else if (types && typeof types === 'object') {
        // Try to pick any array-ish field
        const candidate = Object.values(types).find((v) => Array.isArray(v));
        normalizedTypes = Array.isArray(candidate) ? candidate : [];
      } else {
        normalizedTypes = [];
      }
      setReportTypes(normalizedTypes);
    } catch (error) {
      log.error('Error loading report types:', error);
    }
  };

  const handleGetPromptPreview = async () => {
    if (!selectedPreviewReportType) {
      alert('Veuillez sélectionner un template');
      return;
    }
    try {
      setPreviewLoading(true);
      // Determine if the selected report type is an enterprise type using the report types data
      const selectedTypeObj = (Array.isArray(reportTypes) ? reportTypes : []).find((rt) => {
        const name = typeof rt === 'string' ? rt : (rt?.name || rt?.value || '');
        return name === selectedPreviewReportType;
      });
      const isEnterpriseReport = typeof selectedTypeObj === 'object' ? !!selectedTypeObj.isEnterprise : false;
      const payloadOrType = isEnterpriseReport
        ? { reportType: selectedPreviewReportType, isEnterprise: true }
        : selectedPreviewReportType;
      const result = await adminService.getPromptPreview(payloadOrType);
      setPromptPreview(result);
    } catch (error) {
      log.error('Error getting prompt preview:', error);
      alert('Erreur lors de la récupération du prompt preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await adminService.updateUser(userId, { isActive: !currentStatus });
      loadUsers();
    } catch (error) {
      log.error('Error updating user status:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await adminService.deleteUser(userId);
        loadUsers();
      } catch (error) {
        log.error('Error deleting user:', error);
      }
    }
  };

  const handleUpdateSubscription = async (userId) => {
    const planType = selectedPlan[userId];
    if (!planType) {
      alert('Veuillez sélectionner un plan');
      return;
    }
    try {
      await adminService.updateUserSubscription(userId, planType);
      loadUsers();
      setSelectedPlan({ ...selectedPlan, [userId]: '' });
    } catch (error) {
      log.error('Error updating subscription:', error);
    }
  };

  // Enterprise management functions
  const loadEnterprises = async () => {
    try {
      log.debug('Loading enterprises...');
      const data = await adminService.getAllEnterprises();
      log.debug('Raw API response for enterprises:', data);
      // Normalize response to an array of enterprises
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.data?.enterprises)
            ? data.data.enterprises
            : (Array.isArray(data?.enterprises) ? data.enterprises : []));
      log.debug('Setting enterprises to state:', list);
      setEnterprises(list);
    } catch (error) {
      log.error('Error loading enterprises:', error);
      log.error('Full error details:', error.message, error.stack);
    }
  };

  const loadEnterpriseAdmins = async (enterpriseId) => {
    try {
      const admins = await adminService.getEnterpriseAdmins(enterpriseId);
      setEnterpriseAdmins(admins);
    } catch (error) {
      log.error('Error loading enterprise admins:', error);
    }
  };

  const handleCreateEnterprise = async () => {
    if (!newEnterprise.name || !newEnterprise.shortName) {
      alert('Nom et code court sont requis');
      return;
    }
    try {
      const payload = {
        name: newEnterprise.name,
        shortName: newEnterprise.shortName,
        description: newEnterprise.description,
        maxLicenses: newEnterprise.maxLicenses,
        specialty: newEnterprise.specialty || '',
        settings: {}
      };
      await adminService.createEnterprise(payload);
      setShowCreateEnterprise(false);
      setNewEnterprise({
        name: '',
        shortName: '',
        description: '',
        maxLicenses: 10,
        specialty: ''
      });
      loadEnterprises();
    } catch (error) {
      log.error('Error creating enterprise:', error);
      alert('Erreur lors de la création de l\'entreprise');
    }
  };

  const handleUpdateEnterprise = async (enterpriseId, updates) => {
    try {
      await adminService.updateEnterprise(enterpriseId, updates);
      loadEnterprises();
    } catch (error) {
      log.error('Error updating enterprise:', error);
      alert('Erreur lors de la mise à jour de l\'entreprise');
    }
  };

  const handleSetUserAdmin = async (enterpriseId, userId, isAdmin) => {
    try {
      await adminService.setUserAdminStatus(enterpriseId, userId, isAdmin);
      loadEnterpriseAdmins(enterpriseId);
    } catch (error) {
      log.error('Error updating admin status:', error);
      alert('Erreur lors de la mise à jour du statut admin');
    }
  };

  const loadEnterpriseUsers = async (enterpriseId) => {
    try {
      const users = await enterpriseAdminService.getEnterpriseUsers(enterpriseId);
      const ent = enterprises.find(e => e.id === enterpriseId);
      setSelectedEnterprise(ent);
      setEnterpriseUsers(users);
      setShowUsersModal(true);
    } catch (error) {
      log.error('Error loading enterprise users:', error);
      alert('Erreur lors du chargement des utilisateurs');
    }
  };

  const handleInviteUser = async (enterpriseId) => {
    if (!inviteEmail) return;
    try {
      await enterpriseAdminService.inviteUser(enterpriseId, inviteEmail, false);
      setInviteEmail('');
      loadEnterpriseUsers(enterpriseId);
    } catch (error) {
      log.error('Error inviting user:', error);
      alert('Erreur lors de l\'invitation');
    }
  };

  const handleRemoveUser = async (enterpriseId, userId) => {
    if (!window.confirm('Supprimer cet utilisateur de l\'entreprise ?')) return;
    try {
      await enterpriseAdminService.removeUser(enterpriseId, userId);
      loadEnterpriseUsers(enterpriseId);
    } catch (error) {
      log.error('Error removing user:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleToggleEnterpriseAdmin = async (enterpriseId, userId, isAdmin) => {
    try {
      await enterpriseAdminService.setUserAdmin(enterpriseId, userId, !isAdmin);
      loadEnterpriseUsers(enterpriseId);
    } catch (error) {

      log.error('Error toggling admin:', error);
      alert('Erreur lors du changement de rôle');
    }
  };

  // Broadcast email functions
  const handleSendCustomBroadcast = async () => {
    if (!broadcastSubject || !broadcastHtmlContent || !broadcastTextContent) {
      alert('Veuillez remplir tous les champs (sujet, contenu HTML et texte)');
      return;
    }

    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir envoyer cet email à tous les utilisateurs vérifiés ? Cette action peut prendre 5-10 minutes.'
    );

    if (!confirmed) return;

    try {
      setSendingBroadcast(true);
      setBroadcastResult(null);
      const result = await adminService.broadcastEmail(
        broadcastSubject,
        broadcastHtmlContent,
        broadcastTextContent
      );
      setBroadcastResult(result);
      // Clear form on success
      setBroadcastSubject('');
      setBroadcastHtmlContent('');
      setBroadcastTextContent('');
    } catch (error) {
      log.error('Error sending custom broadcast:', error);
      alert('Erreur lors de l\'envoi du broadcast: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handleSendTechnicalIssue = async () => {
    const confirmed = window.confirm(
      'Envoyer l\'alerte d\'incident technique à tous les utilisateurs vérifiés ?'
    );

    if (!confirmed) return;

    try {
      setSendingBroadcast(true);
      setBroadcastResult(null);
      const result = await adminService.broadcastTechnicalIssue();
      setBroadcastResult(result);
    } catch (error) {
      log.error('Error sending technical issue broadcast:', error);
      alert('Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handleSendIssueResolved = async () => {
    const confirmed = window.confirm(
      'Envoyer la notification de résolution d\'incident à tous les utilisateurs vérifiés ?'
    );

    if (!confirmed) return;

    try {
      setSendingBroadcast(true);
      setBroadcastResult(null);
      const result = await adminService.broadcastIssueResolved();
      setBroadcastResult(result);
    } catch (error) {
      log.error('Error sending issue resolved broadcast:', error);
      alert('Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Convert HTML to plain text
  const htmlToPlainText = (html) => {
    if (!html) return '';

    // Replace common HTML elements with text equivalents
    let text = html
      // Headers
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n$1\n' + '='.repeat(50) + '\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n$1\n' + '-'.repeat(30) + '\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n$1\n')
      // Paragraphs
      .replace(/<p[^>]*>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      // Line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Lists
      .replace(/<ul[^>]*>/gi, '\n')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<ol[^>]*>/gi, '\n')
      .replace(/<\/ol>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      // Links - show URL
      .replace(/<a[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi, '$2 ($1)')
      // Horizontal rules
      .replace(/<hr[^>]*>/gi, '\n' + '-'.repeat(50) + '\n')
      // Bold and italic (keep content)
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '$1')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '$1')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '$1')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '$1')
      // Remove all other HTML tags
      .replace(/<[^>]+>/g, '')
      // Decode HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Clean up multiple newlines
      .replace(/\n{3,}/g, '\n\n')
      // Trim whitespace
      .trim();

    return text;
  };

  // Handle HTML content change and auto-update text content
  const handleHtmlContentChange = (newHtmlContent) => {
    setBroadcastHtmlContent(newHtmlContent);
    // Auto-generate plain text version
    const plainText = htmlToPlainText(newHtmlContent);
    setBroadcastTextContent(plainText);
  };

  // HTML Composer helper functions
  const insertHtmlTag = (tag, placeholder = '') => {
    const content = broadcastHtmlContent;
    let newContent = '';

    switch(tag) {
      case 'h1':
        newContent = content + `<h1>${placeholder || 'Titre Principal'}</h1>\n`;
        break;
      case 'h2':
        newContent = content + `<h2>${placeholder || 'Sous-titre'}</h2>\n`;
        break;
      case 'p':
        newContent = content + `<p>${placeholder || 'Votre paragraphe ici...'}</p>\n`;
        break;
      case 'bold':
        newContent = content + `<strong>${placeholder || 'texte en gras'}</strong>`;
        break;
      case 'italic':
        newContent = content + `<em>${placeholder || 'texte en italique'}</em>`;
        break;
      case 'link':
        newContent = content + `<a href="https://">${placeholder || 'lien'}</a>`;
        break;
      case 'ul':
        newContent = content + `<ul>\n  <li>Élément 1</li>\n  <li>Élément 2</li>\n  <li>Élément 3</li>\n</ul>\n`;
        break;
      case 'ol':
        newContent = content + `<ol>\n  <li>Premier</li>\n  <li>Deuxième</li>\n  <li>Troisième</li>\n</ol>\n`;
        break;
      case 'button':
        newContent = content + `<a href="https://voxia-md.ch" style="display:inline-block;padding:12px 24px;background:#dc2626;color:white;text-decoration:none;border-radius:8px;font-weight:600;">Bouton</a>\n`;
        break;
      case 'divider':
        newContent = content + `<hr style="border:none;border-top:2px solid #e2e8f0;margin:20px 0;">\n`;
        break;
      default:
        newContent = content;
    }

    // Use the handler to update both HTML and text content
    handleHtmlContentChange(newContent);
  };

  const loadTemplate = (templateType) => {
    let htmlTemplate = '';
    let textTemplate = '';

    switch(templateType) {
      case 'announcement':
        htmlTemplate = `<h1>Annonce importante</h1>
<p>Chers utilisateurs de Voxia,</p>
<p>Nous avons le plaisir de vous annoncer...</p>
<ul>
  <li>Nouvelle fonctionnalité 1</li>
  <li>Nouvelle fonctionnalité 2</li>
  <li>Amélioration 3</li>
</ul>
<p>Pour plus d'informations, visitez notre site.</p>
<a href="https://voxia-md.ch" style="display:inline-block;padding:12px 24px;background:#dc2626;color:white;text-decoration:none;border-radius:8px;font-weight:600;margin-top:16px;">En savoir plus</a>
<hr style="border:none;border-top:2px solid #e2e8f0;margin:20px 0;">
<p style="color:#64748b;font-size:14px;">L'équipe Voxia</p>`;
        textTemplate = `Annonce importante

Chers utilisateurs de Voxia,

Nous avons le plaisir de vous annoncer...

- Nouvelle fonctionnalité 1
- Nouvelle fonctionnalité 2
- Amélioration 3

Pour plus d'informations, visitez notre site: https://voxia-md.ch

L'équipe Voxia`;
        break;
      case 'update':
        htmlTemplate = `<h1>Mise à jour de la plateforme</h1>
<p>Bonjour,</p>
<p>Une nouvelle mise à jour de Voxia est disponible avec les améliorations suivantes:</p>
<ol>
  <li>Performance améliorée</li>
  <li>Nouveaux modèles de rapport</li>
  <li>Corrections de bugs</li>
</ol>
<p style="color:#64748b;font-size:14px;">Cordialement,<br>L'équipe Voxia</p>`;
        textTemplate = `Mise à jour de la plateforme

Bonjour,

Une nouvelle mise à jour de Voxia est disponible avec les améliorations suivantes:

1. Performance améliorée
2. Nouveaux modèles de rapport
3. Corrections de bugs

Cordialement,
L'équipe Voxia`;
        break;
      case 'maintenance':
        htmlTemplate = `<h1>Maintenance programmée</h1>
<p>Chers utilisateurs,</p>
<p><strong style="color:#f59e0b;">Une maintenance planifiée aura lieu le [DATE] de [HEURE] à [HEURE].</strong></p>
<p>Pendant cette période, la plateforme sera temporairement indisponible.</p>

<p>Nous vous remercions de votre compréhension.</p>
<p style="color:#64748b;font-size:14px;">L'équipe technique Voxia</p>`;
        textTemplate = `Maintenance Programmée

Chers utilisateurs,

Une maintenance planifiée aura lieu le [DATE] de [HEURE] à [HEURE].

Pendant cette période, la plateforme sera temporairement indisponible.

Nous vous remercions de votre compréhension.

L'équipe technique Voxia`;
        break;
      default:
        return;
    }

    setBroadcastHtmlContent(htmlTemplate);
    setBroadcastTextContent(textTemplate);
  };

  if (!isAdmin) {
    return <div className="admin-dashboard"><h1>Accès refusé</h1></div>;
  }

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Gestion des Utilisateurs
        </button>
        {isAdmin && (
          <>
            <button
              className={activeTab === 'enterprises' ? 'active' : ''}
              onClick={() => setActiveTab('enterprises')}
            >
              Gestion des Entreprises
            </button>
            <button
              className={activeTab === 'promptPreview' ? 'active' : ''}
              onClick={() => setActiveTab('promptPreview')}
            >
              Prompt Preview
            </button>
            <button
              className={activeTab === 'broadcast' ? 'active' : ''}
              onClick={() => setActiveTab('broadcast')}
            >
              Broadcast Email
            </button>
          </>
        )}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="users-section">
          {loading ? (
            <p>Chargement...</p>
          ) : (
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th></th> {/* Chevron column */}
                  <th>ID</th>
                  <th>Email</th>
                  <th>Nom</th>
                  <th>Admin</th>
                  <th>Actif</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <React.Fragment key={u.id}>
                    <tr>
                      <td>
                        <button
                          className="expand-btn"
                          onClick={() => toggleExpandRow(u.id)}
                        >
                          {expandedRows[u.id] ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.fullName}</td>
                    <td>{u.isAdmin ? 'Oui' : 'Non'}</td>
                    <td>{u.isActive ? 'Oui' : 'Non'}</td>
                    <td>{u.planType || 'Aucun'}</td>
                    <td>{u.status || 'Inactif'}</td>
                    <td>
                      <button onClick={() => handleToggleActive(u.id, u.isActive)}>
                        {u.isActive ? 'Désactiver' : 'Activer'}
                      </button>
                      <button onClick={() => handleDeleteUser(u.id)}>Supprimer</button>
                      <select 
                        value={selectedPlan[u.id] || ''} 
                        onChange={(e) => setSelectedPlan({ ...selectedPlan, [u.id]: e.target.value })}
                      >
                        <option value="">Choisir un plan</option>
                        <option value="wood">Wood</option>
                        <option value="bronze">Bronze</option>
                        <option value="silver">Silver</option>
                        <option value="gold">Gold</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                      <button onClick={() => handleUpdateSubscription(u.id)}>Mettre à jour plan</button>
                    </td>
                  </tr>
                  {expandedRows[u.id] && (
                    <tr className="user-details-row">
                      <td colSpan="9">
                        {/* Raw JSON payload */}
                        <pre className="raw-json">
                          {JSON.stringify((rawUsersData.find(r=>r.id===u.id) || u), null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Enterprises Tab */}
      {activeTab === 'enterprises' && isAdmin && (
        <div className="enterprises-section">
          <div className="enterprises-header">
            <h2>Gestion des Entreprises</h2>
            <button 
              
              onClick={() => setShowCreateEnterprise(true)}
            >
              Créer une Entreprise
            </button>
          </div>

          {/* Create Enterprise Modal */}
          {showCreateEnterprise && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>Créer une Nouvelle Entreprise</h3>
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={newEnterprise.name}
                    onChange={(e) => setNewEnterprise({...newEnterprise, name: e.target.value})}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Code court *</label>
                  <input
                    type="text"
                    value={newEnterprise.shortName}
                    onChange={(e) => setNewEnterprise({...newEnterprise, shortName: e.target.value})}
                    placeholder="Code unique (ex: CHU-Cardio)"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newEnterprise.description}
                    onChange={(e) => setNewEnterprise({...newEnterprise, description: e.target.value})}
                    placeholder="Description de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre maximum de licences</label>
                  <input
                    type="number"
                    value={newEnterprise.maxLicenses}
                    onChange={(e) => setNewEnterprise({...newEnterprise, maxLicenses: parseInt(e.target.value)})}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Spécialité</label>
                  <select
                    value={newEnterprise.specialty}
                    onChange={(e) => setNewEnterprise({ ...newEnterprise, specialty: e.target.value })}
                  >
                    <option value="">-- Sélectionnez une spécialité --</option>
                    {SPECIALTIES.map((spec, idx) => (
                      <option key={idx} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  <button onClick={handleCreateEnterprise}>Créer</button>
                  <button onClick={() => setShowCreateEnterprise(false)}>Annuler</button>
                </div>
              </div>
            </div>
          )}

          {/* Enterprises List */}
          <table className="admin-enterprises-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Code court</th>
                <th>Description</th>
                <th>Licences</th>
                <th>Mode de licence</th>
                <th>Utilisation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enterprises.map((enterprise) => (
                <tr key={enterprise.id}>
                  <td>{enterprise.name}</td>
                  <td>{enterprise.shortName}</td>
                  <td>{enterprise.description}</td>
                  <td>{enterprise.usedLicenses} / {enterprise.maxLicenses}</td>
                  <td>{enterprise.licensingMode || '-'}</td>
                  <td>
                    <div className="license-bar">
                      <div 
                        className="license-usage" 
                        style={{width: `${(Number(enterprise.maxLicenses) > 0) ? Math.round((Number(enterprise.usedLicenses || 0) / Number(enterprise.maxLicenses)) * 100) : 0}%`}}
                      ></div>
                    </div>
                    {(Number(enterprise.maxLicenses) > 0) ? Math.round((Number(enterprise.usedLicenses || 0) / Number(enterprise.maxLicenses)) * 100) : 0}%
                  </td>
                  <td>
                    <button 
                      onClick={() => loadEnterpriseUsers(enterprise.id)}
                    >
                      Utilisateurs
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedEnterprise(enterprise);
                        loadEnterpriseAdmins(enterprise.id);
                      }}
                    >
                      Admins
                    </button>
                    <button 
                      onClick={() => {
                        const newMaxLicenses = prompt('Nouveau nombre de licences:', enterprise.maxLicenses);
                        if (newMaxLicenses) {
                          handleUpdateEnterprise(enterprise.id, { maxLicenses: parseInt(newMaxLicenses) });
                        }
                      }}
                    >
                      Modifier Licences
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Enterprise Admins Modal */}
          {selectedEnterprise && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>Administrateurs - {selectedEnterprise.name}</h3>
                <table className="enterprise-admins-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Nom</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enterpriseAdmins.map((admin) => (
                      <tr key={admin.id}>
                        <td>{admin.email}</td>
                        <td>{admin.fullName}</td>
                        <td>
                          <button 
                            onClick={() => handleSetUserAdmin(selectedEnterprise.id, admin.id, false)}
                          >
                            Retirer Admin
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="modal-actions">
                  <button onClick={() => setSelectedEnterprise(null)}>Fermer</button>
                </div>
              </div>
            </div>
          )}

          {showUsersModal && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>Utilisateurs – {enterprises.find(e => e.id === selectedEnterprise?.id)?.name || ''}</h3>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Inviter par email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                  <button onClick={() => handleInviteUser(selectedEnterprise.id)}>Inviter</button>
                </div>
                <table className="enterprise-users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Nom</th>
                      <th>Admin</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enterpriseUsers.map((user) => (
                      <tr key={user.userId}>
                        <td>{user.email}</td>
                        <td>{user.fullName}</td>
                        <td>{user.isAdmin ? 'Oui' : 'Non'}</td>
                        <td>
                          <button onClick={() => handleToggleEnterpriseAdmin(selectedEnterprise.id, user.userId, user.isAdmin)}>
                            {user.isAdmin ? 'Retirer Admin' : 'Définir Admin'}
                          </button>
                          <button onClick={() => handleRemoveUser(selectedEnterprise.id, user.userId)}>Supprimer</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="modal-actions">
                  <button onClick={() => setShowUsersModal(false)}>Fermer</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prompt Preview Tab */}
      {activeTab === 'promptPreview' && isAdmin && (
        <div className="prompt-preview-section" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <label>Modèles:</label>
            <select
              value={selectedPreviewReportType}
              onChange={(e) => setSelectedPreviewReportType(e.target.value)}
            >
              <option value="">Sélectionner un type</option>
              {(Array.isArray(reportTypes) ? reportTypes : []).map((rt) => {
                const name = typeof rt === 'string' ? rt : (rt?.name || rt?.value || '');
                if (!name) return null;
                return (
                  <option key={name} value={name}>{name}</option>
                );
              })}
            </select>
            <button onClick={handleGetPromptPreview} disabled={previewLoading}>
              {previewLoading ? 'Chargement...' : 'Voir Prompt Preview'}
            </button>
          </div>

          {promptPreview && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
              {/* System Prompt */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <h3 style={{ margin: 0 }}>System Prompt</h3>
                  <button
                    onClick={() => {
                      const text = (promptPreview.systemPrompt || promptPreview.systemprompt || promptPreview.system || '') + '';
                      navigator.clipboard?.writeText(text).catch(() => {
                        const area = document.createElement('textarea');
                        area.value = text;
                        document.body.appendChild(area);
                        area.select();
                        document.execCommand('copy');
                        document.body.removeChild(area);
                      });
                    }}
                  >
                    Copier
                  </button>
                </div>
                <pre className="raw-json" style={{ whiteSpace: 'pre-wrap' }}>
{(promptPreview.systemPrompt || promptPreview.systemprompt || promptPreview.system || '')}
                </pre>
              </div>

              {/* User Prompt */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <h3 style={{ margin: 0 }}>User Prompt</h3>
                  <button
                    onClick={() => {
                      const text = (promptPreview.userPrompt || promptPreview.userprompt || promptPreview.user || promptPreview.iserprompt || '') + '';
                      navigator.clipboard?.writeText(text).catch(() => {
                        const area = document.createElement('textarea');
                        area.value = text;
                        document.body.appendChild(area);
                        area.select();
                        document.execCommand('copy');
                        document.body.removeChild(area);
                      });
                    }}
                  >
                    Copier
                  </button>
                </div>
                <pre className="raw-json" style={{ whiteSpace: 'pre-wrap' }}>
{(promptPreview.userPrompt || promptPreview.userprompt || promptPreview.user || promptPreview.iserprompt || '')}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Broadcast Email Tab */}
      {activeTab === 'broadcast' && isAdmin && (
        <div className="broadcast-section">
          <h2>Broadcast Email</h2>

          {/* Quick Action Buttons */}
          <div className="broadcast-quick-actions">
            <h3>Actions Rapides</h3>
            <div className="quick-action-buttons">
              <button
                className="broadcast-btn technical-issue"
                onClick={handleSendTechnicalIssue}
                disabled={sendingBroadcast}
              >
                Alerte Incident Technique
              </button>
              <button
                className="broadcast-btn issue-resolved"
                onClick={handleSendIssueResolved}
                disabled={sendingBroadcast}
              >
                Notification Résolution
              </button>
            </div>
            <p className="broadcast-note">
              Ces boutons envoient des emails pré-définis à tous les utilisateurs vérifiés.
            </p>
          </div>

          {/* Custom Email Form */}
          <div className="broadcast-custom-form">
            <h3>Email Personnalisé</h3>

            {/* Templates */}
            <div className="form-group">
              <label>Modèles pré-définis (optionnel)</label>
              <div className="template-buttons">
                <button
                  type="button"
                  className="template-btn"
                  onClick={() => loadTemplate('announcement')}
                  disabled={sendingBroadcast}
                >
                  📢 Annonce
                </button>
                <button
                  type="button"
                  className="template-btn"
                  onClick={() => loadTemplate('update')}
                  disabled={sendingBroadcast}
                >
                  🔄 Mise à jour
                </button>
                <button
                  type="button"
                  className="template-btn"
                  onClick={() => loadTemplate('maintenance')}
                  disabled={sendingBroadcast}
                >
                  🔧 Maintenance
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Sujet *</label>
              <input
                type="text"
                value={broadcastSubject}
                onChange={(e) => setBroadcastSubject(e.target.value)}
                placeholder="Sujet de l'email"
                disabled={sendingBroadcast}
              />
            </div>

            {/* HTML Composer */}
            <div className="form-group">
              <div className="composer-header">
                <label>Contenu HTML *</label>
                <div className="composer-controls">
                  <button
                    type="button"
                    className={`mode-toggle ${composerMode === 'visual' ? 'active' : ''}`}
                    onClick={() => setComposerMode('visual')}
                    disabled={sendingBroadcast}
                  >
                    🎨 Visual
                  </button>
                  <button
                    type="button"
                    className={`mode-toggle ${composerMode === 'html' ? 'active' : ''}`}
                    onClick={() => setComposerMode('html')}
                    disabled={sendingBroadcast}
                  >
                    &lt;/&gt; HTML
                  </button>
                  <button
                    type="button"
                    className={`mode-toggle ${showHtmlPreview ? 'active' : ''}`}
                    onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                    disabled={sendingBroadcast}
                  >
                    👁️ Preview
                  </button>
                </div>
              </div>

              {/* HTML Formatting Toolbar */}
              {composerMode === 'visual' && (
                <div className="html-toolbar">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('h1')}
                    disabled={sendingBroadcast}
                    title="Titre principal"
                  >
                    H1
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('h2')}
                    disabled={sendingBroadcast}
                    title="Sous-titre"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('p')}
                    disabled={sendingBroadcast}
                    title="Paragraphe"
                  >
                    P
                  </button>
                  <span className="toolbar-divider">|</span>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('bold')}
                    disabled={sendingBroadcast}
                    title="Gras"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('italic')}
                    disabled={sendingBroadcast}
                    title="Italique"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('link')}
                    disabled={sendingBroadcast}
                    title="Lien"
                  >
                    🔗
                  </button>
                  <span className="toolbar-divider">|</span>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('ul')}
                    disabled={sendingBroadcast}
                    title="Liste à puces"
                  >
                    • Liste
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('ol')}
                    disabled={sendingBroadcast}
                    title="Liste numérotée"
                  >
                    1. Liste
                  </button>
                  <span className="toolbar-divider">|</span>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('button')}
                    disabled={sendingBroadcast}
                    title="Bouton CTA"
                  >
                    🔘 Bouton
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertHtmlTag('divider')}
                    disabled={sendingBroadcast}
                    title="Séparateur"
                  >
                    ─
                  </button>
                </div>
              )}

              <textarea
                value={broadcastHtmlContent}
                onChange={(e) => handleHtmlContentChange(e.target.value)}
                placeholder="<h2>Bonjour!</h2><p>Votre message HTML ici...</p>"
                rows={12}
                disabled={sendingBroadcast}
                className="html-textarea"
              />

              {/* HTML Preview */}
              {showHtmlPreview && broadcastHtmlContent && (
                <div className="html-preview">
                  <div className="preview-label">Aperçu:</div>
                  <div
                    className="preview-content"
                    dangerouslySetInnerHTML={{ __html: broadcastHtmlContent }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Contenu Texte * <span className="auto-generated-label">(Auto-généré depuis HTML)</span></label>
              <textarea
                value={broadcastTextContent}
                onChange={(e) => setBroadcastTextContent(e.target.value)}
                placeholder="Version texte de votre message..."
                rows={6}
                disabled={sendingBroadcast}
              />
            </div>
            <button
              className="broadcast-btn custom-broadcast"
              onClick={handleSendCustomBroadcast}
              disabled={sendingBroadcast}
            >
              {sendingBroadcast ? 'Envoi en cours...' : 'Envoyer Email Personnalisé'}
            </button>
            <p className="broadcast-warning">
              ⚠️ Attention: L'envoi peut prendre 5-10 minutes pour 1000 utilisateurs.
            </p>
          </div>

          {/* Loading Indicator */}
          {sendingBroadcast && (
            <div className="broadcast-loading">
              <div className="spinner"></div>
              <p>Envoi en cours... Cela peut prendre plusieurs minutes.</p>
            </div>
          )}

          {/* Results Display */}
          {broadcastResult && (
            <div className={`broadcast-result ${broadcastResult.failedCount > 0 ? 'has-errors' : 'success'}`}>
              <h3>Résultats du Broadcast</h3>
              <div className="result-stats">
                <div className="stat">
                  <span className="stat-label">Utilisateurs totaux:</span>
                  <span className="stat-value">{broadcastResult.totalUsers}</span>
                </div>
                <div className="stat success">
                  <span className="stat-label">Emails envoyés:</span>
                  <span className="stat-value">{broadcastResult.sentCount}</span>
                </div>
                <div className="stat error">
                  <span className="stat-label">Échecs:</span>
                  <span className="stat-value">{broadcastResult.failedCount}</span>
                </div>
              </div>

              {broadcastResult.errors && broadcastResult.errors.length > 0 && (
                <div className="broadcast-errors">
                  <h4>Erreurs:</h4>
                  <ul>
                    {broadcastResult.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
